"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import { FieldControl, FieldLabel, FieldRoot } from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import { verifyAccount } from "../actions";
import { useAppointmentT } from "../i18n/use-t";
import { errorKey } from "../lib/errors";
import { useSapFailureFlags } from "../lib/use-sap-failure-flags";

export default function PreauthGate() {
  const t = useAppointmentT();
  const router = useRouter();
  const sapFailures = useSapFailureFlags();
  const [houseNumber, setHouseNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    houseNumber.trim().length > 0 && accountNumber.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorCode(null);
    const result = await verifyAccount(
      {
        houseNumber: houseNumber.trim(),
        accountNumber: accountNumber.trim(),
      },
      sapFailures,
    );
    if (result.ok) {
      router.refresh();
    } else {
      setErrorCode(result.error);
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* <Card>
        <Card.Header>
          <Card.Title>Under Maintenance</Card.Title>
          <Card.Description>
            We're currently undergoing maintenance. Please check back later.
          </Card.Description>
        </Card.Header>
      </Card> */}

      <Card>
        <Card.Header>
          <Card.Title>{t("gate.title")}</Card.Title>
          <Card.Description>
            {t.list("gate.description").map((line, index) => (
              <p
                className={[0, 1].includes(index) ? "mb-2" : ""}
                key={index.toString()}
              >
                {line}
              </p>
            ))}
            <p className="mb-2 font-semibold">
              {t("gate.descriptionLineItemTitle")}
            </p>
            <ul className="list-disc pl-6">
              {t.list("gate.descriptionLineItems").map((line, index) => (
                <li key={index.toString()}>{line}</li>
              ))}
            </ul>
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <FieldRoot invalid={errorCode !== null} name="preauthHouseNumber">
              <FieldLabel>{t("gate.houseNumberLabel")}</FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    autoFocus={true}
                    className={defaultProps.className ?? ""}
                    onChange={(e) => {
                      setHouseNumber(e.target.value);
                      setErrorCode(null);
                    }}
                    placeholder={t("gate.houseNumberPlaceholder")}
                    style={defaultProps.style ?? {}}
                    value={houseNumber}
                  />
                )}
              />
            </FieldRoot>
            <FieldRoot invalid={errorCode !== null} name="preauthAccountNumber">
              <FieldLabel>{t("gate.accountNumberLabel")}</FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    className={defaultProps.className ?? ""}
                    inputMode="numeric"
                    onChange={(e) => {
                      setAccountNumber(e.target.value);
                      setErrorCode(null);
                    }}
                    placeholder={t("gate.accountNumberPlaceholder")}
                    style={defaultProps.style ?? {}}
                    value={accountNumber}
                  />
                )}
              />
            </FieldRoot>
            {errorCode !== null && (
              <p className="font-medium text-[0.8rem] text-destructive">
                {t(errorKey("gate.errors", errorCode))}
              </p>
            )}
            <Button disabled={!canSubmit || submitting} type="submit">
              {t("gate.submit")}
            </Button>
          </form>
        </Card.Content>
      </Card>
      <div className="mx-2 my-8 md:mx-8">
        <h3 className="mb-4 font-bold text-lg">{t("gate.footerTitle")}</h3>
        <p className="whitespace-pre-wrap text-muted-foreground">
          {t("gate.footerDescription")}
        </p>
      </div>
    </>
  );
}
